import { useEffect, useRef, useState } from 'react';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { useLanguage } from '@/contexts/language-context';

interface VisualMatchImageProps {
  imageUrl: string;
}

export const VisualMatchImage: React.FC<VisualMatchImageProps> = ({
  imageUrl,
}) => {
  const { t } = useLanguage();
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const handleImageError = () => {
    setIsImageLoading(false);
    setImageError(true);
    console.error('[MFA] Failed to load visual match image:', imageUrl);
  };

  // Set loading state when image URL changes (e.g., after resend)
  useEffect(() => {
    if (imageUrl) {
      // Reset loading when the challenge image URL changes.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsImageLoading(true);
      setImageError(false);

      // Check if image is already loaded from cache (e.g., after page refresh)
      if (imgRef.current?.complete && imgRef.current?.naturalHeight !== 0) {
        setIsImageLoading(false);
      }
    } else {
      setIsImageLoading(false);
    }
  }, [imageUrl]);

  return (
    <div className='my-4 flex flex-col items-center gap-2'>
      {imageError ? (
        <div className='w-32 aspect-square flex items-center justify-center bg-error-primary/10 border-2 border-error-primary rounded-lg'>
          <Text
            as='p'
            size={TextSizes.SM}
            className='text-error-primary text-center px-4'
          >
            ⚠️ {t('mfa.imageLoadError')}
          </Text>
        </div>
      ) : (
        <div className='relative w-32 aspect-square'>
          <img
            ref={imgRef}
            key={imageUrl}
            src={imageUrl}
            alt={t('mfa.visualMatchImageAlt')}
            className='w-full h-full object-cover rounded-lg border-2 border-secondary'
            onError={handleImageError}
            onLoad={() => setIsImageLoading(false)}
          />
          {isImageLoading && (
            <div className='absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg'>
              <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-white' />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
